import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as PickingIcon,
} from "@mui/icons-material";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  useTSQSalesOrderList,
  useTSMDeactivateSO,
  useTSMReactivateSO,
  useTSMCompleteSO,
  useTSMVoidSO,
} from "../../hooks/useTSSalesOrder";
import { ListRequestFilter } from "../../types/common/commonTypes";
import {
  PrimaryOrderStatusType,
  SalesOrderFulfillmentStatusType,
} from "../../types/common/statusTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import { SalesOrderFull } from "../../types/salesOrderTypes";
import { NumericDisplayField } from "../../components/common/CustomFields";
import statusUtils from "../../utils/statusUtils";
import stringUtils from "../../utils/stringUtils";
import {
  DynamicRequestFilter,
  DynamicRequestSort,
} from "../../types/dynamicQueryTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import PageHeaderComponent from "../../components/common/PageHeader";
import ListFilterSortPanel, {
  FilterField,
} from "../../components/common/ListFilterSortPanel";

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
  placeholder: string;
}> = ({ finalSet, placeholder }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

interface OrdItemData {
  quant: number;
  eval: boolean;
}

interface OrderData {
  sonum: string;
  soid: string;
  ponum: string;
  cust: string;
  custcode: string;
  ordstat: PrimaryOrderStatusType;
  fillstat: SalesOrderFulfillmentStatusType;
  orderDate: string;
  total: number;
  ordItems: Record<string, OrdItemData>;
}

export const PageSalesOrderList: React.FC = () => {
  const navigate = useNavigate();

  const { user } = useAuth();

  const [error, setError] = useState<string | null>(null);

  const [targetSO, setTargetSO] = useState<string>("");
  const [targetCust, setTargetCust] = useState<string>("");
  const [targetPO, setTargetPO] = useState<string>("");

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const [orderBy, setOrderBy] = useState<string>("ord_status");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const sorts: DynamicRequestSort[] = useMemo(() => {
    switch (orderBy) {
      case "number":
        return [
          {
            table: "sales_orders",
            alt_name: "so",
            field: "number",
            asc: order === "asc",
          },
        ];
      case "customer":
        return [
          {
            table: "customers",
            alt_name: "cust",
            field: "name",
            asc: order === "asc",
          },
          {
            table: "sales_orders",
            alt_name: "so",
            field: "number",
            asc: false,
          },
        ];
      case "ord_status":
        return [
          {
            table: "sales_orders",
            alt_name: "so",
            field: "order_status",
            asc: order === "asc",
          },
          {
            table: "sales_orders",
            alt_name: "so",
            field: "number",
            asc: false,
          },
        ];
      case "fill_status":
        return [
          {
            table: "sales_orders",
            alt_name: "so",
            field: "fulfillment_status",
            asc: order === "asc",
          },
          {
            table: "sales_orders",
            alt_name: "so",
            field: "number",
            asc: false,
          },
        ];
      case "order_date":
        return [
          {
            table: "sales_orders",
            alt_name: "so",
            field: "order_date",
            asc: order === "asc",
          },
        ];
      default:
        return [];
    }
  }, [order, orderBy]);

  const filters: DynamicRequestFilter[][] = useMemo(() => {
    const filts: DynamicRequestFilter[][] = [];
    if (targetCust !== "") {
      filts[0] = [];
      filts[1] = [];
      filts[0].push({
        table: "customers",
        alt_name: "cust",
        field: "name",
        operator: "contains",
        value: targetCust,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "customers",
        alt_name: "cust",
        field: "code",
        operator: "contains",
        value: targetCust,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetSO !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "sales_orders",
        alt_name: "so",
        field: "number",
        operator: "contains",
        value: targetSO,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] !== undefined) {
        filts[1].push({
          table: "sales_orders",
          alt_name: "so",
          field: "number",
          operator: "contains",
          value: targetSO,
          is_not: false,
          inclusive_start: false,
          inclusive_end: false,
          case_insensitive: true,
        });
      }
    }
    if (targetPO !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "sales_orders",
        alt_name: "so",
        field: "customer_po",
        operator: "contains",
        value: targetPO,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] !== undefined) {
        filts[1].push({
          table: "sales_orders",
          alt_name: "so",
          field: "customer_po",
          operator: "contains",
          value: targetPO,
          is_not: false,
          inclusive_start: false,
          inclusive_end: false,
          case_insensitive: true,
        });
      }
    }
    return filts;
  }, [targetSO, targetPO, targetCust]);

  const {
    data: soResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQDynamicList({
    page: pagination.page,
    page_size: pagination.size,
    primary: "sales_orders",
    alt_name: "so",
    primary_fields: [
      "id",
      "number",
      "customer_po",
      "order_date",
      "order_status",
      "fulfillment_status",
    ],
    joins: [
      {
        table: "customers",
        alt_name: "cust",
        fields: ["id", "name", "code"],
        condition: {
          remote_table: "sales_orders",
          alt_name: "so",
          remote_field: "customer_id",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: filters,
    sorts: sorts,
    subqueries: [
      {
        result_name: "so.subtotal",
        aggregation: {
          function: "sum",
          table: "sales_order_line_items",
          alt_name: null,
          field: null,
          expression: "unit_cost * quantity_ordered",
        },
        from_table: "sales_order_line_items",
        from_alt_name: null,
        join_conditions: [
          {
            remote_table: "sales_orders",
            alt_name: "so",
            remote_field: "id",
            local_field: "sales_order_id",
          },
        ],
        filters: null,
      },
    ],
  });

  const [orders, orderTotal]: [OrderData[], number] = useMemo(() => {
    const orders: OrderData[] = [];
    const total = soResponse?.total || 0;
    let ordTemp: Record<string, any> = {};
    if (soResponse?.items) {
      soResponse.items.forEach((val) => {
        orders.push({
          sonum: val["so.number"],
          soid: val["so.id"],
          ponum: val["so.customer_po"],
          cust: val["cust.name"],
          custcode: val["cust.code"],
          ordstat: val["so.order_status"],
          fillstat: val["so.fulfillment_status"],
          orderDate: new Date(val["so.order_date"]).toLocaleDateString("en-US"),
          total: val["so.subtotal"],
          ordItems: {},
        });
      });
    }

    return [orders, total];
  }, [soResponse]);

  const deactivateSOMutation = useTSMDeactivateSO();
  const reactivateSOMutation = useTSMReactivateSO();
  const completeSOMutation = useTSMCompleteSO();
  const voidSOMutation = useTSMVoidSO();

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this SO?")) {
      return;
    }

    try {
      await deactivateSOMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate SO:", err);
      setError(err.response?.data?.detail || "Failed to deactivate SO");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateSOMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate SO:", err);
      setError(err.response?.data?.detail || "Failed to reactivate SO");
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const completeOrder = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot Complete Order");
      return;
    }
    try {
      await completeSOMutation.mutateAsync(id);
    } catch (e: any) {
      setError("There was an error marking SO Complete");
      console.error(e);
      return;
    }
  };

  const voidOrder = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot VOID Order");
      return;
    }
    try {
      await voidSOMutation.mutateAsync(id);
    } catch (e: any) {
      setError("There was an error marking SO VOID");
      console.error(e);
      return;
    }
  };

  const handleCreateButtonClicked: React.MouseEventHandler<
    HTMLButtonElement
  > = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (event.button === 1) {
      window.open("/orders/sales/form?mode=create", "_blank");
    } else if (event.button === 0) {
      navigate("/orders/sales/form?mode=create");
    }
  };

  return (
    <Stack direction="column" spacing={1}>
      <PageHeaderComponent
        headerTextContent={{
          text: "Sales Orders",
        }}
        user={user}
        otherButtons={[
          {
            id: "create-new-so",
            text: "Create New",
            variant: "contained",
            onClick: handleCreateButtonClicked,
            startIcon: <AddIcon />,
            props: { color: "info" },
          },
          // {
          //   id: "split-button",
          //   options: [
          //     {
          //       id: "create-new-so",
          //       text: "Create New",
          //       variant: "contained",
          //       onClick: handleCreateButtonClicked,
          //       startIcon: <AddIcon />,
          //     },
          //     {
          //       id: "bing-bong",
          //       text: "Bing Bong",
          //       variant: "contained",
          //       onClick: handleCreateButtonClicked,
          //       startIcon: <AddIcon />,
          //     },
          //   ],
          // },
        ]}
        debugPrintValues={{ orders }}
      />

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" flexWrap={"wrap"} gap={1}>
          <FilterBox finalSet={setTargetSO} placeholder="Filter By SO Number" />
          <FilterBox
            finalSet={setTargetPO}
            placeholder="Filter By Customer PO"
          />
          <FilterBox
            finalSet={setTargetCust}
            placeholder="Filter By Customer Name/Code"
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={orderTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={orderBy === "number" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "number"}
                  direction={orderBy === "number" ? order : "asc"}
                  onClick={() => handleSort("number")}
                >
                  Order Numbers
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "customer" ? order : false}
                width="20%"
              >
                <TableSortLabel
                  active={orderBy === "customer"}
                  direction={orderBy === "customer" ? order : "asc"}
                  onClick={() => handleSort("customer")}
                >
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "ord_status" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "ord_status"}
                  direction={orderBy === "ord_status" ? order : "asc"}
                  onClick={() => handleSort("ord_status")}
                >
                  Order Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "fill_status" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "fill_status"}
                  direction={orderBy === "fill_status" ? order : "asc"}
                  onClick={() => handleSort("fill_status")}
                >
                  Fulfillment Status
                </TableSortLabel>
              </TableCell>
              <TableCell width="15%">Subtotal</TableCell>
              <TableCell
                sortDirection={orderBy === "order_date" ? order : false}
                width="12%"
              >
                <TableSortLabel
                  active={orderBy === "order_date"}
                  direction={orderBy === "order_date" ? order : "asc"}
                  onClick={() => handleSort("order_date")}
                >
                  Order Date
                </TableSortLabel>
              </TableCell>
              <TableCell width="8%">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {filters.length > 0
                      ? "No SOs found matching your search."
                      : "No Sales Orders found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((so) => (
                <TableRow key={so.soid} hover>
                  <TableCell>
                    <Stack direction="column" gap={0.5}>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        SO: {so.sonum}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        PO: {so.ponum}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" gap={0.5}>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Name: {so.cust}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Code: {so.custcode}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(so.ordstat)}
                      color={statusUtils.getPrimaryOrderStatusColor(so.ordstat)}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stringUtils.capFirstLetter(so.fillstat)}
                      color={statusUtils.getSoFulfillmentStatusColor(
                        so.fillstat,
                      )}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <NumericDisplayField
                      value={so.total}
                      isCurrency
                      decimalPlaces={4}
                      typographyProps={{ variant: "body1" }}
                    />
                  </TableCell>
                  <TableCell>{so.orderDate}</TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={1}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/orders/sales/form?mode=view&id=${so.soid}`,
                              )
                            }
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit SO">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/orders/sales/form?mode=edit&id=${so.soid}`,
                              )
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!["COMPLETE", "VOID"].includes(so.ordstat) && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Complete SO">
                              <IconButton
                                size="small"
                                onClick={() => completeOrder(so.soid)}
                                color="success"
                                disabled={completeSOMutation.isPending}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="VOID SO">
                              <IconButton
                                size="small"
                                onClick={() => voidOrder(so.soid)}
                                color="error"
                                disabled={voidSOMutation.isPending}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={orderTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Stack>
  );
};
